/* Woojer app deck — animated program cards, ported 1:1 from Kfir's Claude Design
   components (Card - *.dc.html) to vanilla JS.

   PERFORMANCE CONTRACT (Kfir, 23.9 — "don't do something heavy or stupid"):
   - ONE requestAnimationFrame for the whole deck, and it advances ONLY the focused card.
   - Every other card paints a single static frame and then stops.
   - Canvases are allocated lazily for cards near the centre and released (width=0) when
     they drift away, so phone memory stays bounded no matter how many cards there are.
   - An IntersectionObserver stops the loop whenever the deck is off-screen.
   - prefers-reduced-motion: static frames only, no loop, ever.
   - The WebGL card (Wooju) builds its context only on a pointer-capable wide screen and
     only while focused; everywhere else its canvas layer is simply absent and the 2D
     gradients carry the card.
*/
(function () {
  'use strict';

  var W = 358, H = 340;                       // native card size everything is drawn at
  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function dpr() { return Math.min(2, window.devicePixelRatio || 1); }
  function rgba(hex, a) {
    var n = parseInt(hex.slice(1), 16);
    return 'rgba(' + (n >> 16) + ',' + ((n >> 8) & 255) + ',' + (n & 255) + ',' + a + ')';
  }
  // roundRect shim: Safari < 16 lacks it and these cards lean on it heavily
  if (!CanvasRenderingContext2D.prototype.roundRect) {
    CanvasRenderingContext2D.prototype.roundRect = function (x, y, w, h, r) {
      if (w < 2 * r) r = w / 2; if (h < 2 * r) r = h / 2;
      this.beginPath();
      this.moveTo(x + r, y);
      this.arcTo(x + w, y, x + w, y + h, r);
      this.arcTo(x + w, y + h, x, y + h, r);
      this.arcTo(x, y + h, x, y, r);
      this.arcTo(x, y, x + w, y, r);
      this.closePath();
      return this;
    };
  }

  /* ---------------------------------------------------------------- renderers
     Each renderer is { draw(ctx, T) } where T is seconds. Pure, stateless where
     possible, so a static frame is just draw(ctx, someFixedT).                */

  var R = {};

  // One pass of each card's own animation, in seconds. A focused card plays exactly
  // one pass from the beginning and then rests on its final frame (Kfir, 24.9:
  // "it needs to start playing the animation one time, not in a loop").
  var CYCLE = {
    wooju: 8, breath: 19, frequency: 14, wakeup: 12,
    nap: 13, wellbeing: 10, snore: 12, market: 12
  };

  /* ---- Frequency producer -------------------------------------------- */
  R.frequency = {
    presets: [40, 60, 100, 174],
    reset: function () { this.f = 40; },
    draw: function (x, T) {
      var L = 22, Rr = 336, A = '#e0915a';
      if (!isFinite(this.f)) this.f = 40;
      x.clearRect(0, 0, W, H);
      var idx = ((Math.floor(T / 3.5) % 4) + 4) % 4, target = this.presets[idx];
      this.f += (target - this.f) * 0.06;
      var f = this.f;
      x.textAlign = 'left'; x.fillStyle = '#f4ede8';
      x.font = '200 46px -apple-system, "SF Pro Display", sans-serif';
      var num = f.toFixed(1); x.fillText(num, L, 96);
      var nw = x.measureText(num).width;
      x.font = '500 13px "IBM Plex Mono", monospace'; x.fillStyle = A; x.fillText('Hz', L + nw + 6, 96);
      x.textAlign = 'right'; x.font = '500 9px "IBM Plex Mono", monospace'; x.letterSpacing = '1.5px'; x.fillStyle = '#857970';
      x.fillText('SINE', Rr, 70); x.fillText('T = ' + (1000 / f).toFixed(1) + ' ms', Rr, 84); x.fillText('WINDOW 50 ms', Rr, 98);
      x.letterSpacing = '0px';
      var top = 116, h = 84, mid = top + h / 2, i, j;
      x.strokeStyle = '#2a2322'; x.lineWidth = 1;
      for (i = 0; i <= 10; i++) { var gx = L + (Rr - L) * i / 10; x.beginPath(); x.moveTo(gx, top); x.lineTo(gx, top + h); x.stroke(); }
      for (j = 0; j <= 4; j++) { var gy = top + h * j / 4; x.beginPath(); x.moveTo(L, gy); x.lineTo(Rr, gy); x.stroke(); }
      var ph = T * 1.2;
      function trace(alpha, lw, blur) {
        x.save(); x.strokeStyle = A; x.globalAlpha = alpha; x.lineWidth = lw; x.shadowColor = A; x.shadowBlur = blur;
        x.beginPath();
        for (var px = 0; px <= Rr - L; px++) {
          var ts = px / (Rr - L) * 0.05, y = mid - Math.sin(2 * Math.PI * f * ts - ph) * (h * 0.38);
          px ? x.lineTo(L + px, y) : x.moveTo(L + px, y);
        }
        x.stroke(); x.restore();
      }
      trace(0.25, 5, 14); trace(1, 1.4, 6);
      var ry = 238;
      function lg(v) { return Math.log(v) / Math.log(250); }
      function pos(v) { return L + (Rr - L) * lg(v); }
      for (var v = 1; v <= 250; v++) {
        var dec = (v === 1 || v === 10 || v === 100);
        var minor = v < 10 || (v < 100 && v % 10 === 0) || v % 50 === 0 || v === 250;
        if (!minor) continue;
        x.strokeStyle = '#3a312e'; x.lineWidth = 1;
        x.beginPath(); x.moveTo(pos(v), ry); x.lineTo(pos(v), ry + (dec ? 9 : 5)); x.stroke();
      }
      x.font = '500 9px "IBM Plex Mono", monospace'; x.fillStyle = '#6f645e'; x.textAlign = 'center';
      [[1, '1'], [10, '10'], [100, '100'], [250, '250']].forEach(function (p) {
        x.fillText(p[1], Math.min(pos(p[0]), Rr - 8), ry + 22);
      });
      var g = x.createLinearGradient(L, 0, pos(f), 0);
      g.addColorStop(0, 'rgba(224,145,90,0)'); g.addColorStop(1, 'rgba(224,145,90,0.9)');
      x.fillStyle = g; x.fillRect(L, ry - 3, pos(f) - L, 2);
      var self = this;
      this.presets.forEach(function (p, i2) {
        x.fillStyle = i2 === idx ? A : '#4a3f3a'; x.beginPath(); x.arc(pos(p), ry - 10, 1.6, 0, 7); x.fill();
      });
      x.save(); x.fillStyle = A; x.shadowColor = A; x.shadowBlur = 10; x.beginPath(); x.arc(pos(f), ry - 2, 4, 0, 7); x.fill(); x.restore();
    }
  };

  /* ---- Breath training ------------------------------------------------ */
  R.breath = {
    draw: function (x, T) {
      var cx = 179, cy = 128;
      x.clearRect(0, 0, W, H);
      var P = [
        { name: '4-7-8', ph: [['INHALE', 4, 0], ['HOLD', 7, 1], ['EXHALE', 8, 2]] },
        { name: 'BOX', ph: [['INHALE', 4, 0], ['HOLD', 4, 1], ['EXHALE', 4, 2], ['HOLD', 4, 3]] },
        { name: 'COHERENT', ph: [['INHALE', 5.5, 0], ['EXHALE', 5.5, 2]] }
      ];
      var tot = P.map(function (q) { return q.ph.reduce(function (a, b) { return a + b[1]; }, 0); });
      var all = tot.reduce(function (a, b) { return a + b; }, 0);
      var tt = T % all, pi = 0;
      while (tt >= tot[pi]) { tt -= tot[pi]; pi++; }
      var pat = P[pi], C = tot[pi], t = tt;
      function e(q) { return 0.5 - 0.5 * Math.cos(Math.PI * q); }
      var ph = 0, acc = 0;
      while (t >= acc + pat.ph[ph][1]) { acc += pat.ph[ph][1]; ph++; }
      var p = (t - acc) / pat.ph[ph][1], kind = pat.ph[ph][2];
      var s = kind === 0 ? 0.5 + 0.5 * e(p) : kind === 1 ? 1 : kind === 2 ? 1 - 0.5 * e(p) : 0.5;
      var pal = ['#7fc29a', '#c7dfb0', '#5f9a7a', '#8fb5a0'], col = pal[kind];
      var cols = pat.ph.map(function (q) { return pal[q[2]]; });
      var durs = pat.ph.map(function (q) { return q[1]; });
      var names = pat.ph.map(function (q) { return q[0]; });
      x.font = '500 10px "IBM Plex Mono", monospace'; x.letterSpacing = '2px'; x.textAlign = 'right';
      x.fillStyle = '#7fc29a'; x.fillText(pat.name + ' · ' + (60 / C).toFixed(1) + ' BR/MIN', 336, 28);
      x.letterSpacing = '0px';
      var n = 96, ends = [], s2 = 0, i;
      durs.forEach(function (d) { s2 += d; ends.push(s2); });
      for (i = 0; i < n; i++) {
        var a = -Math.PI / 2 + i / n * Math.PI * 2, ti = i / n * C;
        var k = 0; for (k = 0; k < ends.length; k++) { if (ti < ends[k]) break; }
        if (k >= ends.length) k = ends.length - 1;
        var lit = ti <= t, major = i % 5 === 0, r1 = 84, r2 = major ? 92 : 88;
        x.strokeStyle = lit ? cols[k] : '#2f2826'; x.globalAlpha = lit ? (major ? 1 : 0.7) : 1; x.lineWidth = major ? 1.4 : 1;
        x.beginPath(); x.moveTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1); x.lineTo(cx + Math.cos(a) * r2, cy + Math.sin(a) * r2); x.stroke();
      }
      x.globalAlpha = 1;
      var r = 70 * s;
      var g = x.createRadialGradient(cx, cy, 0, cx, cy, r * 1.25);
      g.addColorStop(0, rgba(col, 0.3)); g.addColorStop(0.6, rgba(col, 0.1)); g.addColorStop(1, rgba(col, 0));
      x.fillStyle = g; x.beginPath(); x.arc(cx, cy, r * 1.25, 0, 7); x.fill();
      for (var j = 0; j < 6; j++) {
        var rr = r * (1 - j * 0.15), amp = (kind === 1 || kind === 3) ? 1.4 : 0.7;
        x.strokeStyle = rgba(col, 0.75 - j * 0.11); x.lineWidth = j === 0 ? 1.5 : 1;
        x.beginPath();
        for (var q = 0; q <= 140; q++) {
          var aa = q / 140 * Math.PI * 2;
          var w = rr + amp * Math.sin(aa * 6 + T * 2 + j * 1.3) + amp * 0.6 * Math.sin(aa * 11 - T * 1.4);
          var px = cx + Math.cos(aa) * w, py = cy + Math.sin(aa) * w;
          q ? x.lineTo(px, py) : x.moveTo(px, py);
        }
        x.stroke();
      }
      x.fillStyle = rgba(col, 0.45);
      for (i = 0; i < 54; i++) {
        var ang = i * 2.399 + T * 0.06 * (i % 2 ? 1 : -1), dd = r + 6 + ((i * 37) % 11);
        if (dd > 80) continue;
        x.beginPath(); x.arc(cx + Math.cos(ang) * dd, cy + Math.sin(ang) * dd, 1, 0, 7); x.fill();
      }
      x.textAlign = 'center'; x.letterSpacing = '2px';
      x.font = '500 10px "IBM Plex Mono", monospace'; x.fillStyle = col; x.fillText(names[ph], cx + 1, cy - 10);
      x.letterSpacing = '0px';
      x.font = '300 30px -apple-system, "SF Pro Display", sans-serif'; x.fillStyle = '#f4ede8';
      x.fillText(String(Math.max(1, Math.ceil(durs[ph] * (1 - p)))), cx, cy + 22);
      var y = 240, x0 = 22, gap = 6, avail = 314 - gap * (durs.length - 1), xs = x0;
      x.textAlign = 'left'; x.font = '500 9px "IBM Plex Mono", monospace'; x.letterSpacing = '1.5px';
      for (var k2 = 0; k2 < durs.length; k2++) {
        var w2 = durs[k2] / C * avail, fr = k2 < ph ? 1 : k2 === ph ? p : 0;
        x.fillStyle = '#2c2524'; x.roundRect(xs, y, w2, 3, 2); x.fill();
        if (fr > 0) { x.fillStyle = cols[k2]; x.roundRect(xs, y, w2 * fr, 3, 2); x.fill(); }
        x.fillStyle = k2 === ph ? cols[k2] : '#6f645e'; x.fillText(names[k2] + ' ' + durs[k2], xs, y + 18);
        xs += w2 + gap;
      }
      x.letterSpacing = '0px';
    }
  };

  /* ---- Power nap ------------------------------------------------------ */
  R.nap = {
    stage: function (m) {
      if (m < 2.5) return 0;
      if (m < 6) return 1 + 0.15 * Math.sin(m * 3);
      if (m < 19.2) return 2 - (m > 11 && m < 12.2 ? 0.8 * Math.sin((m - 11) / 1.2 * Math.PI) : 0) + 0.06 * Math.sin(m * 5);
      return 2 - 2 * Math.min(1, (m - 19.2) / 0.8);
    },
    draw: function (x, T) {
      var L = 22, X0 = 72, Rr = 336, P = '#9d93c9', O = '#e0915a', cyc = 13, t = T % cyc;
      var m = Math.min(20, t / 10.5 * 20), done = t > 10.5, self = this;
      x.clearRect(0, 0, W, H);
      x.textAlign = 'left'; x.fillStyle = '#f4ede8'; x.font = '200 34px -apple-system, "SF Pro Display", sans-serif';
      var mm = Math.floor(m), ss = Math.floor((m - mm) * 60);
      var clock = String(mm).padStart(2, '0') + ':' + String(ss).padStart(2, '0');
      x.fillText(clock, L, 78);
      var cw = x.measureText(clock).width;
      x.font = '500 11px "IBM Plex Mono", monospace'; x.fillStyle = '#6f645e'; x.fillText('/ 20:00', L + cw + 8, 78);
      x.letterSpacing = '1.5px'; x.font = '500 9px "IBM Plex Mono", monospace';
      x.fillStyle = done ? O : P;
      x.fillText(done ? 'WAKING · GENTLE PULSE' : ['AWAKE', 'N1 · DRIFTING', 'N2 · LIGHT SLEEP'][Math.round(Math.min(2, this.stage(m)))], L, 96);
      var lanes = ['AWAKE', 'N1', 'N2', 'N3'], y0 = 122, gap = 30;
      function Y(s) { return y0 + s * gap; }
      function Xm(v) { return X0 + (Rr - X0) * v / 20; }
      x.fillStyle = 'rgba(157,147,201,0.05)'; x.fillRect(X0, Y(3) - 11, Rr - X0, 22);
      x.save(); x.beginPath(); x.rect(X0, Y(3) - 11, Rr - X0, 22); x.clip();
      x.strokeStyle = 'rgba(157,147,201,0.12)'; x.lineWidth = 1;
      for (var i = -30; i < 300; i += 7) { x.beginPath(); x.moveTo(X0 + i, Y(3) + 11); x.lineTo(X0 + i + 22, Y(3) - 11); x.stroke(); }
      x.restore();
      x.textAlign = 'right'; x.fillStyle = '#6f645e'; x.fillText('DEEP SLEEP AVOIDED', Rr - 6, Y(3) + 3);
      lanes.forEach(function (n, i2) {
        x.textAlign = 'left'; x.fillStyle = '#6f645e'; x.fillText(n, L, Y(i2) + 3);
        if (i2 < 3) { x.strokeStyle = '#2a2530'; x.setLineDash([2, 4]); x.beginPath(); x.moveTo(X0, Y(i2)); x.lineTo(Rr, Y(i2)); x.stroke(); x.setLineDash([]); }
      });
      x.save(); x.strokeStyle = P; x.lineWidth = 1.8; x.shadowColor = P; x.shadowBlur = 8; x.lineJoin = 'round';
      x.beginPath();
      for (var v = 0; v <= m; v += 0.05) { var px = Xm(v), py = Y(self.stage(v)); v ? x.lineTo(px, py) : x.moveTo(px, py); }
      x.stroke(); x.restore();
      var hx = Xm(m), hy = Y(this.stage(m));
      x.fillStyle = done ? O : '#f4ede8'; x.beginPath(); x.arc(hx, hy, 3.5, 0, 7); x.fill();
      var wx = Xm(20);
      x.strokeStyle = 'rgba(224,145,90,0.5)'; x.beginPath(); x.moveTo(wx, Y(0) - 12); x.lineTo(wx, Y(3) + 11); x.stroke();
      if (done) for (var k = 0; k < 3; k++) {
        var php = (T * 1.2 + k / 3) % 1;
        x.strokeStyle = 'rgba(224,145,90,' + (1 - php) + ')'; x.beginPath(); x.arc(wx, Y(0), 4 + php * 16, 0, 7); x.stroke();
      }
      x.fillStyle = '#6f645e'; x.textAlign = 'left'; x.fillText('0', X0, Y(3) + 28);
      x.textAlign = 'center'; x.fillText('10 MIN', Xm(10), Y(3) + 28);
      x.textAlign = 'right'; x.fillStyle = O; x.fillText('WAKE 20', Rr, Y(3) + 28);
      x.letterSpacing = '0px';
    }
  };

  /* ---- Snoring detection ---------------------------------------------- */
  R.snore = {
    h: function (n) { var s = Math.sin(n * 127.1 + 311.7) * 43758.5; return s - Math.floor(s); },
    level: function (b) { var r = this.h(b); return r < 0.4 ? 0 : r < 0.72 ? 1 : 2; },
    amp: function (tt) {
      var P = 4, b = Math.floor(tt / P), p = tt - b * P, lv = this.level(b);
      var base = [0.06, 0.42, 0.9][lv];
      var env = p < 1.7 ? Math.sin(Math.PI * p / 1.7) : 0.12 * Math.sin(Math.PI * (p - 1.7) / 2.3);
      return { a: Math.max(0.02, base * env * (0.75 + 0.25 * this.h(Math.floor(tt * 30)))), lv: p < 1.7 ? lv : 0, b: b, p: p };
    },
    draw: function (x, T) {
      var L = 22, Rr = 336, mid = 150, Hh = 62, span = 24, n = 96, step = (Rr - L) / n, i;
      x.clearRect(0, 0, W, H);
      x.strokeStyle = '#2a2530'; x.lineWidth = 1; x.setLineDash([2, 4]);
      [mid - Hh, mid + Hh].forEach(function (y) { x.beginPath(); x.moveTo(L, y); x.lineTo(Rr, y); x.stroke(); });
      x.setLineDash([]);
      var off = (T * (n / span)) % 1;
      for (i = 0; i <= n; i++) {
        var tt = T - span + (i + (1 - off)) * span / n, px = L + (i - off) * step;
        if (px < L) continue;
        var r = this.amp(tt), hh = Math.max(1.5, r.a * Hh);
        x.fillStyle = ['#3a3542', '#5e5775', '#b4aaf0'][r.lv];
        x.fillRect(px, mid - hh, Math.max(1.2, step - 1.4), hh * 2);
      }
      var b0 = Math.floor((T - span) / 4), b1 = Math.floor(T / 4);
      for (var b = b0; b <= b1; b++) {
        if (this.level(b) === 2 && this.level(b - 1) >= 1) {
          var tn = b * 4 + 2.2, px2 = Rr - (T - tn) / span * (Rr - L);
          if (px2 < L || px2 > Rr) continue;
          x.strokeStyle = '#e0915a'; x.lineWidth = 1.5; x.beginPath(); x.arc(px2, mid, 4, 0, 7); x.stroke();
          x.strokeStyle = 'rgba(224,145,90,0.35)'; x.lineWidth = 1; x.beginPath(); x.moveTo(px2, mid - Hh - 6); x.lineTo(px2, mid - 5); x.stroke();
        }
      }
      var g = x.createLinearGradient(L, 0, L + 40, 0);
      g.addColorStop(0, 'rgba(26,22,26,1)'); g.addColorStop(1, 'rgba(26,22,26,0)');
      x.fillStyle = g; x.fillRect(L - 2, mid - Hh - 8, 42, Hh * 2 + 16);
      x.fillStyle = '#b4aaf0'; x.fillRect(Rr + 2, mid - Hh, 1, Hh * 2);
      var cur = this.amp(T), db = Math.round(30 + 34 * Math.min(1, cur.a / 0.9));
      x.font = '500 9px "IBM Plex Mono", monospace'; x.letterSpacing = '1.5px'; x.textAlign = 'left'; x.fillStyle = '#6f645e';
      x.fillText('−24 s', L, mid + Hh + 16); x.textAlign = 'right'; x.fillText('NOW', Rr, mid + Hh + 16);
      var sy = 256; x.textAlign = 'left';
      [['LIVE', db + ' dB'], ['BAND', '40–300 Hz'], ['BREATH', '15 /min']].forEach(function (p, i2) {
        var sx = L + i2 * 108;
        x.fillStyle = '#6f645e'; x.font = '500 9px "IBM Plex Mono", monospace'; x.fillText(p[0], sx, sy);
        x.fillStyle = i2 === 0 ? '#f4ede8' : '#c9bdb4'; x.font = '500 13px "IBM Plex Mono", monospace'; x.fillText(p[1], sx, sy + 17);
      });
      x.letterSpacing = '0px';
    }
  };

  /* ---- Vibroacoustic wakeup ------------------------------------------- */
  R.wakeup = {
    draw: function (x, T) {
      var L = 22, Rr = 336, A = '#f0b060', hz = 168, cyc = 12, t = T % cyc;
      var p = Math.min(1, t / 10), atAlarm = t >= 10;
      x.clearRect(0, 0, W, H);
      var sky = x.createLinearGradient(0, 40, 0, hz);
      sky.addColorStop(0, 'rgba(240,176,96,0)'); sky.addColorStop(1, 'rgba(240,140,70,' + (0.05 + 0.2 * p) + ')');
      x.fillStyle = sky; x.fillRect(0, 40, W, hz - 40);
      var sx = 179, sy = hz + 34 - 74 * p, sr = 30;
      x.save(); x.beginPath(); x.rect(0, 0, W, hz); x.clip();
      var gl = x.createRadialGradient(sx, sy, sr * 0.6, sx, sy, sr * 3.2);
      gl.addColorStop(0, 'rgba(240,176,96,' + (0.25 + 0.3 * p) + ')'); gl.addColorStop(1, 'rgba(240,176,96,0)');
      x.fillStyle = gl; x.beginPath(); x.arc(sx, sy, sr * 3.2, 0, 7); x.fill();
      var sg = x.createLinearGradient(0, sy - sr, 0, sy + sr);
      sg.addColorStop(0, '#ffd9a0'); sg.addColorStop(1, '#e0804a');
      x.fillStyle = sg; x.beginPath(); x.arc(sx, sy, sr, 0, 7); x.fill();
      for (var i = 1; i <= 3; i++) {
        var ph = (T * 0.9 * (0.5 + p) + i / 3) % 1;
        x.strokeStyle = 'rgba(240,176,96,' + ((1 - ph) * 0.5 * (0.3 + p)) + ')'; x.lineWidth = 1;
        x.beginPath(); x.arc(sx, sy, sr + 6 + ph * 60, 0, 7); x.stroke();
      }
      x.restore();
      x.strokeStyle = '#3a2f2a'; x.lineWidth = 1; x.beginPath(); x.moveTo(L, hz); x.lineTo(Rr, hz); x.stroke();
      var mins = Math.floor(30 * p), hh = mins === 30 ? '07' : '06';
      var mm = mins === 30 ? '00' : String(30 + mins).padStart(2, '0');
      x.textAlign = 'left'; x.fillStyle = '#f4ede8'; x.font = '200 34px -apple-system, "SF Pro Display", sans-serif';
      x.fillText(hh + ':' + mm, L, 78);
      x.font = '500 9px "IBM Plex Mono", monospace'; x.letterSpacing = '1.5px';
      x.fillStyle = atAlarm ? A : '#857970';
      x.fillText(atAlarm ? 'WAKE' : 'PULSE ' + Math.round(8 + 92 * p * p) + '%', L, 96);
      var by = 214, n = 48, bw = (Rr - L) / n;
      for (var k = 0; k < n; k++) {
        var q = k / (n - 1), lit = q <= p, amp = 4 + 36 * q * q;
        var live = lit && q > p - 0.08 ? 0.6 + 0.4 * Math.sin(T * (6 + 14 * q)) : 1;
        var h2 = amp * (lit ? live : 1);
        x.fillStyle = lit ? 'rgba(240,176,96,' + (0.35 + 0.65 * q) + ')' : '#2c2524';
        x.fillRect(L + k * bw + 1, by - h2 / 2, bw - 2.2, h2);
      }
      x.fillStyle = '#6f645e';
      x.fillText('06:30', L, by + 42); x.textAlign = 'center'; x.fillText('LOW · SLOW', 179, by + 42);
      x.textAlign = 'right'; x.fillText('07:00', Rr, by + 42);
      x.letterSpacing = '0px';
    }
  };

  /* ---- Wellbeing ------------------------------------------------------ */
  R.wellbeing = {
    mins: [12, 20, 8, 26, 18, 30, 22],
    reads: [42, 48, 45, 56, 58, 66, 71],
    draw: function (x, T) {
      var C = '#ec6a64', L = 22, Rr = 336, cyc = 10, t = T % cyc;
      function e(q) { return 1 - Math.pow(1 - Math.max(0, Math.min(1, q)), 3); }
      var fade = t > cyc - 0.6 ? (cyc - t) / 0.6 : 1;
      x.clearRect(0, 0, W, H);
      var gx = 88, gy = 128, gr = 56, a0 = Math.PI * 0.75, sweep = Math.PI * 1.5, i;
      for (i = 0; i <= 50; i++) {
        var a = a0 + sweep * i / 50, major = i % 10 === 0;
        x.strokeStyle = major ? '#4a3a3a' : '#2e2626'; x.lineWidth = 1;
        x.beginPath();
        x.moveTo(gx + Math.cos(a) * (gr + 8), gy + Math.sin(a) * (gr + 8));
        x.lineTo(gx + Math.cos(a) * (gr + (major ? 14 : 11)), gy + Math.sin(a) * (gr + (major ? 14 : 11)));
        x.stroke();
      }
      x.strokeStyle = '#2c2424'; x.lineWidth = 4; x.lineCap = 'round';
      x.beginPath(); x.arc(gx, gy, gr, a0, a0 + sweep); x.stroke();
      var score = this.reads[6], v = e(t / 2.2) * score / 100 * fade;
      x.save(); x.strokeStyle = C; x.shadowColor = C; x.shadowBlur = 10;
      x.beginPath(); x.arc(gx, gy, gr, a0, a0 + sweep * Math.max(0.001, v)); x.stroke(); x.restore();
      var ea = a0 + sweep * v; x.fillStyle = '#f4ede8';
      x.beginPath(); x.arc(gx + Math.cos(ea) * gr, gy + Math.sin(ea) * gr, 3.5, 0, 7); x.fill();
      x.lineCap = 'butt';
      x.textAlign = 'center'; x.fillStyle = '#f4ede8'; x.font = '200 40px -apple-system, "SF Pro Display", sans-serif';
      x.fillText(String(Math.round(v * 100)), gx, gy + 12);
      x.font = '500 9px "IBM Plex Mono", monospace'; x.letterSpacing = '1.5px'; x.fillStyle = '#857970';
      x.fillText('READ', gx + 1, gy + 30);
      var cx0 = 176, cw = Rr - cx0, top = 74, bot = 196, n = 7, bw = cw / n;
      var days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'], pts = [];
      x.textAlign = 'left'; x.fillStyle = '#857970'; x.fillText('MINUTES FELT', cx0, top - 6);
      for (i = 0; i < n; i++) {
        var q = e((t - 0.4 - i * 0.12) / 0.9) * fade;
        var h2 = (bot - top - 30) * this.mins[i] / 30 * q, bx = cx0 + i * bw + bw * 0.28;
        x.fillStyle = i === 6 ? 'rgba(236,106,100,0.85)' : 'rgba(236,106,100,0.28)';
        x.roundRect(bx, bot - h2, bw * 0.44, Math.max(1, h2), 3); x.fill();
        x.fillStyle = i === 6 ? '#f4ede8' : '#6f645e'; x.textAlign = 'center';
        x.fillText(days[i], bx + bw * 0.22, bot + 16);
        pts.push([bx + bw * 0.22, bot - 18 - (bot - top - 40) * (this.reads[i] - 40) / 35]);
      }
      var lp = e((t - 1.4) / 1.4) * fade;
      if (lp > 0) {
        x.save(); x.beginPath(); x.rect(cx0, 0, cw * lp, H); x.clip();
        x.strokeStyle = '#f4ede8'; x.lineWidth = 1.2; x.beginPath();
        pts.forEach(function (p, i2) { i2 ? x.lineTo(p[0], p[1]) : x.moveTo(p[0], p[1]); });
        x.stroke();
        pts.forEach(function (p) {
          x.fillStyle = '#161211'; x.beginPath(); x.arc(p[0], p[1], 2.6, 0, 7); x.fill();
          x.strokeStyle = '#f4ede8'; x.stroke();
        });
        x.restore();
      }
      var sy = 244; x.textAlign = 'left';
      [['SESSIONS', '6'], ['MINUTES', '136'], ['TREND', '+29']].forEach(function (p, i2) {
        var sx = L + i2 * 108;
        x.fillStyle = '#6f645e'; x.font = '500 9px "IBM Plex Mono", monospace'; x.fillText(p[0], sx, sy);
        x.fillStyle = i2 === 2 ? C : '#f4ede8'; x.font = '500 13px "IBM Plex Mono", monospace'; x.fillText(p[1], sx, sy + 17);
      });
      x.letterSpacing = '0px';
    }
  };

  /* ---- Woojer Market -------------------------------------------------- */
  R.market = {
    items: [
      ['TRACK', 'Morning Clarity 528 Hz', '30:00'],
      ['GEAR', 'Vest 4', (window.wjDeckPrice||{})['vest-4'] || ''],
      ['PROGRAM', 'Breath training', '4-7-8'],
      ['TRACK', 'Grounding 174Hz', '30:00'],
      ['GEAR', 'Strap 4', (window.wjDeckPrice||{})['strap-4'] || ''],
      ['PROGRAM', 'Power nap', '20 min'],
      ['GEAR', 'MAT', (window.wjDeckPrice||{})['mat'] || ''],
      ['TRACK', 'Happiness and Relief 432 Hz', '30:00']
    ],
    col: { TRACK: '#e0915a', GEAR: '#c9bdb4', PROGRAM: '#7fc29a' },
    icon: function (x, type, cx, cy, c, T) {
      x.strokeStyle = c; x.fillStyle = c; x.lineWidth = 1.4; x.lineCap = 'round';
      if (type === 'TRACK') {
        for (var i = 0; i < 5; i++) {
          var h = 4 + 8 * Math.abs(Math.sin(T * 3 + i * 1.3));
          x.beginPath(); x.moveTo(cx - 8 + i * 4, cy - h / 2); x.lineTo(cx - 8 + i * 4, cy + h / 2); x.stroke();
        }
      } else if (type === 'GEAR') {
        x.roundRect(cx - 7, cy - 8, 14, 16, 3); x.stroke();
        x.beginPath(); x.arc(cx, cy, 3, 0, 7); x.stroke();
      } else {
        x.beginPath(); x.arc(cx, cy, 7, -Math.PI / 2, -Math.PI / 2 + Math.PI * 1.5); x.stroke();
        x.beginPath(); x.arc(cx, cy - 7, 1.6, 0, 7); x.fill();
      }
      x.lineCap = 'butt';
    },
    draw: function (x, T) {
      var L = 22, Rr = 336, cy = 140, rowH = 40, n = this.items.length, step = 2.4, self = this;
      x.clearRect(0, 0, W, H);
      var k = T / step, base = Math.floor(k), f = k - base;
      var ease = f < 0.75 ? 0 : (function (q) { return q * q * (3 - 2 * q); })((f - 0.75) / 0.25);
      var pos = base + ease;
      x.fillStyle = 'rgba(224,145,90,0.07)'; x.strokeStyle = 'rgba(224,145,90,0.35)'; x.lineWidth = 1;
      x.roundRect(L - 4, cy - 20, Rr - L + 8, 40, 12); x.fill(); x.stroke();
      for (var j = -3; j <= 3; j++) {
        var idx = ((Math.round(pos) + j) % n + n) % n;
        var off = j - (pos - Math.round(pos)), y = cy + off * rowH, dist = Math.abs(off);
        if (dist > 2.3) continue;
        var it = this.items[idx], type = it[0], name = it[1], meta = it[2], c = this.col[type];
        var a = Math.max(0, 1 - dist * 0.38), s = 1 - dist * 0.06;
        x.save(); x.globalAlpha = a; x.translate(179, y); x.scale(s, s); x.translate(-179, -y);
        x.fillStyle = 'rgba(255,255,255,0.04)'; x.roundRect(L + 6, y - 14, 28, 28, 8); x.fill();
        self.icon(x, type, L + 20, y, c, T);
        x.textAlign = 'left'; x.font = '500 8px "IBM Plex Mono", monospace'; x.letterSpacing = '1.5px'; x.fillStyle = c;
        x.fillText(type, L + 46, y - 3); x.letterSpacing = '0px';
        x.font = '500 14px -apple-system, "SF Pro Text", sans-serif'; x.fillStyle = '#f4ede8'; x.fillText(name, L + 46, y + 13);
        x.textAlign = 'right'; x.font = '500 11px "IBM Plex Mono", monospace';
        x.fillStyle = dist < 0.5 ? '#f4ede8' : '#857970'; x.fillText(meta, Rr - 8, y + 5);
        x.restore();
      }
      var fg = x.createLinearGradient(0, 40, 0, 248);
      fg.addColorStop(0, 'rgba(22,18,17,1)'); fg.addColorStop(0.18, 'rgba(22,18,17,0)');
      fg.addColorStop(0.82, 'rgba(22,18,17,0)'); fg.addColorStop(1, 'rgba(22,18,17,1)');
      x.fillStyle = fg; x.fillRect(0, 40, W, 208);
      var cats = ['TRACKS', 'PROGRAMS', 'GEAR'], cur = this.items[((Math.round(pos)) % n + n) % n][0];
      x.font = '500 9px "IBM Plex Mono", monospace'; x.letterSpacing = '1.5px'; x.textAlign = 'left';
      var cx = L;
      cats.forEach(function (cn) {
        var on = cn.indexOf(cur) === 0;
        x.fillStyle = on ? '#f4ede8' : '#5a504b'; x.fillText(cn, cx, 262);
        if (on) { x.fillStyle = '#e0915a'; x.fillRect(cx, 268, x.measureText(cn).width - 1.5, 1.5); }
        cx += x.measureText(cn).width + 18;
      });
      x.letterSpacing = '0px';
    }
  };

  /* ---- Wooju (2D base + optional WebGL sphere) -------------------------- */
  R.wooju = {
    S: [
      ['Stressed after work', 'Grounding 174Hz', 'PAIN RELIEF · 30 MIN'],
      ["Can't wind down tonight", "7.83 Hz Earth's Frequency", 'RESTORE · 20 MIN'],
      ['Slow start this morning', 'Morning Clarity 528 Hz', 'MINDFULNESS · 30 MIN']
    ],
    draw: function (x, T) {
      var A = '#e0915a', cx = 179, L = 22, Rr = 336;
      var dt = Math.min(0.05, T - (this.last == null ? T : this.last)); this.last = T;
      x.clearRect(0, 0, W, H);
      var cyc = 8, k = Math.floor(T / cyc) % 3, t = T % cyc, s = this.S[k];
      var feel = s[0], track = s[1], meta = s[2];
      var thinking = t > 2.6 && t < 3.8, ready = t >= 3.8;
      this.en = (this.en == null ? 0 : this.en) + ((thinking ? 1 : 0) - (this.en == null ? 0 : this.en)) * Math.min(1, dt * 4);
      this.ph = (this.ph == null ? 0 : this.ph) + dt * (0.5 + 1.6 * this.en);
      this.rot = (this.rot == null ? 0 : this.rot) + dt * (0.12 + 0.5 * this.en);
      var rad = 70, cyo = 100, self = this;
      [[-0.45, '70,150,255', 0.16], [0.45, '255,200,165', 0.12]].forEach(function (p) {
        var g = x.createRadialGradient(cx + p[0] * rad, cyo, rad * 0.4, cx + p[0] * rad, cyo, rad * 1.5);
        g.addColorStop(0, 'rgba(' + p[1] + ',' + (p[2] + 0.08 * self.en) + ')');
        g.addColorStop(1, 'rgba(' + p[1] + ',0)');
        x.fillStyle = g; x.beginPath(); x.arc(cx + p[0] * rad, cyo, rad * 1.5, 0, 7); x.fill();
      });
      var core = x.createRadialGradient(cx, cyo, 0, cx, cyo, rad);
      core.addColorStop(0, 'rgba(6,10,26,0.95)'); core.addColorStop(0.85, 'rgba(10,16,34,0.85)'); core.addColorStop(1, 'rgba(10,16,34,0)');
      x.fillStyle = core; x.beginPath(); x.arc(cx, cyo, rad, 0, 7); x.fill();
      if (this.gl) this.drawGL();
      var iy = 186;
      x.fillStyle = 'rgba(255,255,255,0.04)'; x.strokeStyle = '#3a2f2a'; x.lineWidth = 1;
      x.roundRect(L, iy, Rr - L, 38, 19); x.fill(); x.stroke();
      var n = Math.min(feel.length, Math.floor(Math.max(0, t - 0.3) * 13)), typed = feel.slice(0, n);
      x.font = '400 14px -apple-system, "SF Pro Text", sans-serif'; x.textAlign = 'left'; x.fillStyle = '#f4ede8';
      x.fillText('“' + typed + (n === feel.length ? '”' : ''), L + 16, iy + 24);
      if (n < feel.length && Math.floor(T * 2.5) % 2 === 0) {
        var w = x.measureText('“' + typed).width;
        x.fillStyle = A; x.fillRect(L + 17 + w, iy + 12, 1.5, 16);
      }
      x.font = '500 9px "IBM Plex Mono", monospace'; x.letterSpacing = '1.5px';
      if (thinking) {
        x.fillStyle = A; x.fillText('LISTENING' + new Array(1 + Math.floor(T * 4) % 3 + 1).join('.'), L, iy + 60);
      } else if (ready) {
        var a = Math.min(1, (t - 3.8) / 0.5), fade = t > cyc - 0.5 ? (cyc - t) / 0.5 : 1;
        x.globalAlpha = a * fade;
        x.fillStyle = A; x.fillText('SESSION READY', L, iy + 60);
        x.fillStyle = '#857970'; x.textAlign = 'right'; x.fillText(meta, Rr, iy + 60); x.textAlign = 'left';
        x.letterSpacing = '0px'; x.font = '500 15px -apple-system, "SF Pro Text", sans-serif'; x.fillStyle = '#f4ede8';
        x.fillText(track, L, iy + 80 - 6 * (1 - a));
        x.globalAlpha = 1;
      }
      x.letterSpacing = '0px';
    },
    /* WebGL sphere: heavy (150x360 line segments), so the deck only calls initGL on a
       wide pointer-capable screen, and only for the focused card. */
    initGL: function (canvas) {
      var d = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = W * d; canvas.height = 200 * d;
      var g = null;
      try { g = canvas.getContext('webgl', { premultipliedAlpha: true, antialias: true, alpha: true }); } catch (e) { g = null; }
      if (!g) return false;
      var NOISE = 'vec3 m289(vec3 x){return x-floor(x*(1./289.))*289.;}vec4 m289(vec4 x){return x-floor(x*(1./289.))*289.;}' +
        'vec4 perm(vec4 x){return m289(((x*34.)+1.)*x);}vec4 tis(vec4 r){return 1.79284291400159-0.85373472095314*r;}' +
        'float snoise(vec3 v){const vec2 C=vec2(1./6.,1./3.);const vec4 D=vec4(0.,.5,1.,2.);' +
        'vec3 i=floor(v+dot(v,C.yyy));vec3 x0=v-i+dot(i,C.xxx);vec3 g=step(x0.yzx,x0.xyz);vec3 l=1.-g;' +
        'vec3 i1=min(g.xyz,l.zxy);vec3 i2=max(g.xyz,l.zxy);vec3 x1=x0-i1+C.xxx;vec3 x2=x0-i2+C.yyy;vec3 x3=x0-D.yyy;' +
        'i=m289(i);vec4 p=perm(perm(perm(i.z+vec4(0.,i1.z,i2.z,1.))+i.y+vec4(0.,i1.y,i2.y,1.))+i.x+vec4(0.,i1.x,i2.x,1.));' +
        'float n_=0.142857142857;vec3 ns=n_*D.wyz-D.xzx;vec4 j=p-49.*floor(p*ns.z*ns.z);vec4 x_=floor(j*ns.z);vec4 y_=floor(j-7.*x_);' +
        'vec4 x=x_*ns.x+ns.yyyy;vec4 y=y_*ns.x+ns.yyyy;vec4 h=1.-abs(x)-abs(y);vec4 b0=vec4(x.xy,y.xy);vec4 b1=vec4(x.zw,y.zw);' +
        'vec4 s0=floor(b0)*2.+1.;vec4 s1=floor(b1)*2.+1.;vec4 sh=-step(h,vec4(0.));vec4 a0=b0.xzyw+s0.xzyw*sh.xxyy;vec4 a1=b1.xzyw+s1.xzyw*sh.zzww;' +
        'vec3 p0=vec3(a0.xy,h.x);vec3 p1=vec3(a0.zw,h.y);vec3 p2=vec3(a1.xy,h.z);vec3 p3=vec3(a1.zw,h.w);' +
        'vec4 nm=tis(vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3)));p0*=nm.x;p1*=nm.y;p2*=nm.z;p3*=nm.w;' +
        'vec4 m=max(.6-vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)),0.);m=m*m;' +
        'return 42.*dot(m*m,vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)));}';
      var vs = 'attribute vec3 aP;uniform float uT,uR,uA;uniform vec2 uS;varying vec3 vC;varying float vA;' + NOISE +
        'void main(){vec3 p=aP;float cr=cos(uR),sr=sin(uR);' +
        'float n1=snoise(p*.9+vec3(0.,0.,uT*.2));float n2=snoise(p*1.8+vec3(uT*.15,-uT*.1,4.));' +
        'vec3 q=p*(1.+uA*(.28*n1+.1*n2));float eq=1.-p.y*p.y;' +
        'q.y+=eq*(uA*1.7*snoise(p*vec3(1.1,1.7,1.1)+vec3(9.,uT*.25,2.))+uA*.45*snoise(p*vec3(2.2,3.,2.2)+vec3(uT*.2,1.,7.)));' +
        'float X=q.x*cr-q.z*sr;float Z=q.x*sr+q.z*cr;float nz=p.x*sr+p.z*cr;float f=1.-abs(nz);float rim=pow(f,3.2);' +
        'float front=step(0.,nz);vA=mix(.02,.055,front)+rim*mix(.45,1.05,front)+.05*front*pow(f,1.2);' +
        'float u=smoothstep(-.6,.7,X+.2*q.y);vC=mix(vec3(.2,.6,1.),vec3(1.,.84,.7),u);' +
        'vC=mix(vC,vec3(1.),.35*pow(rim,4.)*front);gl_Position=vec4(vec2(X,q.y)*uS,0.,1.);}';
      var fs = 'precision mediump float;varying vec3 vC;varying float vA;void main(){gl_FragColor=vec4(vC*vA,vA);}';
      function sh(t, src) {
        var o = g.createShader(t); g.shaderSource(o, src); g.compileShader(o);
        if (!g.getShaderParameter(o, g.COMPILE_STATUS)) return null;
        return o;
      }
      var vso = sh(g.VERTEX_SHADER, vs), fso = sh(g.FRAGMENT_SHADER, fs);
      if (!vso || !fso) return false;
      var pr = g.createProgram(); g.attachShader(pr, vso); g.attachShader(pr, fso); g.linkProgram(pr);
      if (!g.getProgramParameter(pr, g.LINK_STATUS)) return false;
      g.useProgram(pr);
      // density by viewport: the full 150x360 mesh is ~108k vertices, far too much for
      // a phone, but a 80x180 mesh reads identically at that size
      var wide = window.matchMedia('(min-width: 750px)').matches;
      var RINGS = wide ? 150 : 80, SEG = wide ? 360 : 180, v = [];
      for (var i = 1; i < RINGS; i++) {
        var th = Math.PI * i / RINGS, y = Math.cos(th), r = Math.sin(th);
        for (var j = 0; j < SEG; j++) {
          var a0 = j / SEG * Math.PI * 2, a1 = (j + 1) / SEG * Math.PI * 2;
          v.push(Math.cos(a0) * r, y, Math.sin(a0) * r, Math.cos(a1) * r, y, Math.sin(a1) * r);
        }
      }
      this.nv = v.length / 3;
      var b = g.createBuffer(); g.bindBuffer(g.ARRAY_BUFFER, b); g.bufferData(g.ARRAY_BUFFER, new Float32Array(v), g.STATIC_DRAW);
      var loc = g.getAttribLocation(pr, 'aP'); g.enableVertexAttribArray(loc); g.vertexAttribPointer(loc, 3, g.FLOAT, false, 0, 0);
      this.u = { t: g.getUniformLocation(pr, 'uT'), r: g.getUniformLocation(pr, 'uR'), a: g.getUniformLocation(pr, 'uA'), s: g.getUniformLocation(pr, 'uS') };
      g.viewport(0, 0, canvas.width, canvas.height); g.enable(g.BLEND); g.blendFunc(g.ONE, g.ONE);
      this.gl = g;
      return true;
    },
    dropGL: function () {
      /* deliberately a no-op: see the note above about one-context-per-canvas */
    },
    drawGL: function () {
      var g = this.gl; if (!g) return;
      g.clearColor(0, 0, 0, 0); g.clear(g.COLOR_BUFFER_BIT);
      g.uniform1f(this.u.t, this.ph || 0); g.uniform1f(this.u.r, this.rot || 0);
      g.uniform1f(this.u.a, 0.09 + 0.04 * (this.en || 0));
      var rad = 70; g.uniform2f(this.u.s, rad / 179, rad / 100);
      g.drawArrays(g.LINES, 0, this.nv);
    }
  };

  /* ---------------------------------------------------------------- deck */

  function Deck(root) {
    this.root = root;
    this.cards = Array.prototype.slice.call(root.querySelectorAll('.wdk__card'));
    this.n = this.cards.length;
    this.active = Math.min(parseInt(root.getAttribute('data-start') || '0', 10) || 0, this.n - 1);
    this.raf = null;
    this.visible = false;
    this.t0 = performance.now();
    this.bind();
    this.layout();
    this.observe();
  }

  Deck.prototype.bind = function () {
    var self = this;
    this.cards.forEach(function (el, i) {
      el.addEventListener('click', function () { self.go(i); });
      el.addEventListener('keydown', function (ev) {
        if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); self.go(i); }
        else if (ev.key === 'ArrowRight') { ev.preventDefault(); self.go(self.active + 1); }
        else if (ev.key === 'ArrowLeft') { ev.preventDefault(); self.go(self.active - 1); }
      });
    });
    var prev = this.root.querySelector('.wdk__prev'), next = this.root.querySelector('.wdk__next');
    if (prev) prev.addEventListener('click', function () { self.go(self.active - 1); });
    if (next) next.addEventListener('click', function () { self.go(self.active + 1); });
    // pointer drag: grab the deck and throw it, mouse or finger
    var stage = this.root.querySelector('.wdk__stage');
    var px = 0, py = 0, dragging = false, moved = false, startActive = 0;
    stage.addEventListener('pointerdown', function (e) {
      if (e.button != null && e.button !== 0) return;
      dragging = true; moved = false;
      px = e.clientX; py = e.clientY; startActive = self.active;
      stage.classList.add('is-dragging');
    });
    window.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - px, dy = e.clientY - py;
      if (Math.abs(dx) < 6 || Math.abs(dy) > Math.abs(dx)) return;
      moved = true;
      var card = self.cards[self.active].getBoundingClientRect().width || 300;
      var steps = Math.round(-dx / (card * 0.55));
      if (steps !== 0 && startActive + steps !== self.active) self.go(startActive + steps);
    });
    function endDrag() {
      if (!dragging) return;
      dragging = false;
      stage.classList.remove('is-dragging');
      // a drag must not also fire the card's click
      if (moved) { stage.addEventListener('click', swallow, true); setTimeout(function () {
        stage.removeEventListener('click', swallow, true); }, 0); }
    }
    function swallow(e) { e.preventDefault(); e.stopPropagation(); }
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);

    // touch swipe on the stage
    var sx = 0, sy = 0, tracking = false;
    stage.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      sx = e.touches[0].clientX; sy = e.touches[0].clientY; tracking = true;
    }, { passive: true });
    stage.addEventListener('touchend', function (e) {
      if (!tracking) return; tracking = false;
      var t = e.changedTouches[0], dx = t.clientX - sx, dy = t.clientY - sy;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) self.go(self.active + (dx < 0 ? 1 : -1));
    }, { passive: true });
  };

  Deck.prototype.observe = function () {
    var self = this;
    if (!('IntersectionObserver' in window)) { this.visible = true; this.start(); return; }
    new IntersectionObserver(function (entries) {
      var nowVisible = entries[0].isIntersecting;
      if (nowVisible === self.visible) return;
      self.visible = nowVisible;
      if (nowVisible) {
        // replay from the top each time the deck comes into view, so the focused card
        // is animating at the moment you arrive rather than already finished
        self.stop();
        self.t0 = performance.now();
        self.start();
      } else {
        self.stop();
      }
    }, { threshold: 0.35 }).observe(this.root);
  };

  // A card gets a live canvas only when it is near the centre; everything else is released.
  Deck.prototype.radius = function () {
    return window.matchMedia('(min-width: 750px)').matches ? 3 : 1;
  };
  Deck.prototype.near = function (i) {
    var d = Math.abs(i - this.active);
    return Math.min(d, this.n - d) <= this.radius();
  };

  Deck.prototype.ctx = function (i) {
    var el = this.cards[i], cv = el.querySelector('canvas.wdk__cv');
    if (!cv) return null;
    if (!cv._ready) {
      var d = dpr();
      cv.width = W * d; cv.height = H * d;
      var c = cv.getContext('2d'); c.scale(d, d);
      cv._ctx = c; cv._ready = true;
    }
    return cv._ctx;
  };

  Deck.prototype.release = function (i) {
    var cv = this.cards[i].querySelector('canvas.wdk__cv');
    if (cv && cv._ready) { cv.width = 0; cv.height = 0; cv._ready = false; cv._ctx = null; }
    /* the GL layer is left alone: its context cannot be rebuilt once released */
  };

  Deck.prototype.renderer = function (i) {
    var key = this.cards[i].getAttribute('data-card');
    return R[key] || null;
  };

  // one static frame, drawn at a flattering moment in each card's cycle
  Deck.prototype.still = function (i) {
    var r = this.renderer(i), c = this.ctx(i);
    if (!r || !c) return;
    var poster = parseFloat(this.cards[i].getAttribute('data-poster') || '4');
    try { r.draw(c, poster); } catch (e) { /* a broken card must never break the page */ }
  };

  Deck.prototype.layout = function () {
    var self = this;
    var n = this.n, rad = this.radius();
    this.cards.forEach(function (el, i) {
      // shortest signed distance around the ring, so the fan is always symmetric
      // about the focused card instead of piling up on one side
      var d = i - self.active;
      if (d > n / 2) d -= n;
      if (d < -n / 2) d += n;
      var ad = Math.abs(d);
      var on = d === 0;
      el.classList.toggle('is-active', on);
      el.setAttribute('aria-hidden', ad > rad ? 'true' : 'false');
      el.tabIndex = on ? 0 : -1;
      // fan: each step out shifts, shrinks, rotates and drops behind
      el.style.setProperty('--d', String(d));
      el.style.setProperty('--ad', String(ad));
      el.style.setProperty('--scrim', String(Math.min(0.30, ad * 0.10)));
      el.style.zIndex = String(100 - ad);
      el.style.pointerEvents = ad > rad ? 'none' : 'auto';
      el.classList.toggle('is-far', ad > rad);
      if (self.near(i)) { if (!el._painted) { self.still(i); el._painted = true; } }
      else if (el._painted) { self.release(i); el._painted = false; }
    });
    var lab = this.root.querySelector('.wdk__label');
    if (lab) lab.textContent = this.cards[this.active].getAttribute('data-title') || '';
    var dots = this.root.querySelectorAll('.wdk__dot');
    for (var k = 0; k < dots.length; k++) dots[k].classList.toggle('is-on', k === this.active);
  };

  Deck.prototype.go = function (i) {
    // endless: always wrap, in both directions
    i = ((i % this.n) + this.n) % this.n;
    if (i === this.active) return;
    // hand the previous card a final static frame so it does not freeze mid-motion
    var prev = this.active;
    this.active = i;
    this.t0 = performance.now();
    // the GL context is built once and kept; nothing to tear down when focus moves
    this.layout();
    if (this.near(prev)) this.still(prev);
    // stop first: start() bails out when a loop is already running, which would skip
    // the new card's reset() and its WebGL init (the sphere never came back)
    this.stop();
    this.start();
  };

  Deck.prototype.start = function () {
    if (REDUCED || this.raf || !this.visible) return;
    var self = this;
    var r = this.renderer(this.active);
    if (r && r.reset) r.reset();
    // WebGL only on a wide pointer-capable screen, and only for the focused card
    if (r && r.initGL) {
      var g = this.cards[this.active].querySelector('canvas.wdk__gl');
      if (g && !g._owner) { if (r.initGL(g)) { g._owner = r; g.classList.add('is-live'); } }
    }
    var key = this.cards[this.active].getAttribute('data-card');
    var span = CYCLE[key] || 12;
    var tick = function (now) {
      var c = self.ctx(self.active), rr = self.renderer(self.active);
      var t = (now - self.t0) / 1000;
      if (!isFinite(t) || t < 0) t = 0;   // never feed a renderer a negative or NaN clock
      if (c && rr) {
        try { rr.draw(c, Math.min(t, span)); } catch (e) { self.stop(); return; }
      }
      if (t >= span) {
        self.raf = null;
        self.still(self.active);   // settle on the good-looking still, not the last frame
        return;
      }
      self.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  };

  Deck.prototype.stop = function () {
    if (this.raf) { cancelAnimationFrame(this.raf); this.raf = null; }
  };

  function init() {
    var nodes = document.querySelectorAll('.wdk:not([data-wdk-ready])');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].setAttribute('data-wdk-ready', '1');
      try { new Deck(nodes[i]); } catch (e) { /* never break the page */ }
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  // Shopify theme editor re-renders sections without a reload
  document.addEventListener('shopify:section:load', init);
})();
